import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BeakerIcon, BuildingOffice2Icon, ClockIcon, DocumentTextIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import RoleOnly from "../../../routes/roleOnly";
import { fetchCompany } from "../../../api/company";
import { fetchHourSettings } from "../../../api/hour";
import { fetchAbsenceTypes } from "../../../api/absence/type";
import { fetchWorkSchedules } from "../../../api/workSchedules";
import { getChemicalInventorySettings } from "../../../api/hse/chemicalInventorySettings";
import { fetchHseSetupStatus } from "../../../api/hse/setupStatus";
import { fetchOfferSettings } from "../../../api/offer-settings";
import { DashboardSetupAlertLink } from "../DashboardSetupAlertLink";
import {
  chemicalInventorySetupIssues,
  companyProfileIssues,
  hoursSetupIssues,
} from "./setupGuideStatus";
import { hseKsSetupIssues } from "./hseKsSetupStatus";
import { normalizeChemicalInventorySettings } from "../../../utils/chemicalInventoryAccess";
import { offerSetupIssues } from "./OfferSetupStatus";

/**
 * Forsiden (dashboard): viser kun kort når admin har konkrete oppgaver i firma-, timer- eller tilbudsoppsett.
 */
export const DashboardAdminSetupAlerts = () => {
  const { t } = useTranslation();
  const currentCompany = useSelector((state) => state.session.currentCompany);

  const hoursModuleOn =
    !Array.isArray(currentCompany?.active_modules) ||
    currentCompany.active_modules.length === 0 ||
    currentCompany.active_modules.includes("hours");

  const offersModuleOn =
    !Array.isArray(currentCompany?.active_modules) ||
    currentCompany.active_modules.length === 0 ||
    currentCompany.active_modules.includes("offers");

  const [loading, setLoading] = useState(true);
  const [companyRecord, setCompanyRecord] = useState(null);
  const [hoursState, setHoursState] = useState({
    hourSettings: null,
    absenceTypeCount: null,
    workScheduleMeta: null,
    loadError: false,
  });
  const [hseSettings, setHseSettings] = useState(null);
  const [hseLoadError, setHseLoadError] = useState(false);
  const [chemicalState, setChemicalState] = useState({
    settings: null,
    loadError: false,
  });
  const [offersState, setOffersState] = useState({
    flags: null,
    recommended: [],
    loadError: false,
  });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const cRes = await fetchCompany();
        if (cancelled) return;
        const cNorm = cRes?.data !== undefined ? cRes.data : cRes;
        const companyOk = cNorm && typeof cNorm === "object" && cNorm.id != null && !cNorm.errors;
        setCompanyRecord(companyOk ? cNorm : null);

        if (offersModuleOn) {
          try {
            const osRes = await fetchOfferSettings();
            if (cancelled) return;
            const osData =
              osRes?.data?.data !== undefined
                ? osRes.data.data
                : osRes?.data !== undefined
                  ? osRes.data
                  : {};
            setOffersState({
              flags: osData?.flags && typeof osData.flags === "object" ? osData.flags : {},
              recommended: Array.isArray(osData?.recommended) ? osData.recommended : [],
              loadError: false,
            });
          } catch {
            if (!cancelled) {
              setOffersState({ flags: null, recommended: [], loadError: true });
            }
          }
        } else {
          setOffersState({ flags: null, recommended: [], loadError: false });
        }

        if (!hoursModuleOn) {
          setHoursState({
            hourSettings: null,
            absenceTypeCount: null,
            workScheduleMeta: null,
            loadError: false,
          });
        } else {
          try {
            const [hsRes, atRes] = await Promise.all([
              fetchHourSettings(),
              fetchAbsenceTypes({ page: 1, per_page: 1, sortBy: "created_at", sort: "DESC" }),
            ]);
            if (cancelled) return;
            const hsData =
              hsRes?.data?.data !== undefined
                ? hsRes.data.data
                : hsRes?.data !== undefined
                  ? hsRes.data
                  : {};
            const total = atRes?.totalCount ?? atRes?.data?.totalCount;
            let wsMeta = null;
            try {
              const wsRes = await fetchWorkSchedules();
              wsMeta = wsRes?.meta && typeof wsRes.meta === "object" ? wsRes.meta : null;
            } catch {
              wsMeta = null;
            }
            if (cancelled) return;
            setHoursState({
              hourSettings: typeof hsData === "object" ? hsData : {},
              absenceTypeCount: typeof total === "number" ? total : null,
              workScheduleMeta: wsMeta,
              loadError: false,
            });
          } catch {
            if (!cancelled) {
              setHoursState({
                hourSettings: null,
                absenceTypeCount: null,
                workScheduleMeta: null,
                loadError: true,
              });
            }
          }
        }

        try {
          const hse = await fetchHseSetupStatus();
          if (!cancelled) {
            setHseSettings(hse && typeof hse === "object" ? hse : null);
            setHseLoadError(false);
          }
        } catch {
          if (!cancelled) {
            setHseSettings(null);
            setHseLoadError(true);
          }
        }

        try {
          const chemRes = await getChemicalInventorySettings();
          if (cancelled) return;
          setChemicalState({
            settings: normalizeChemicalInventorySettings(chemRes),
            loadError: false,
          });
        } catch {
          if (!cancelled) {
            setChemicalState({ settings: null, loadError: true });
          }
        }
      } catch {
        if (!cancelled) setCompanyRecord(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [hoursModuleOn, offersModuleOn, currentCompany?.id]);

  const companyIssues = useMemo(() => companyProfileIssues(companyRecord), [companyRecord]);
  const hoursIssues = useMemo(
    () =>
      hoursModuleOn
        ? hoursSetupIssues({
            hourSettings: hoursState.hourSettings,
            absenceTypeCount: hoursState.absenceTypeCount,
            workScheduleMeta: hoursState.workScheduleMeta,
            loadError: hoursState.loadError,
          })
        : [],
    [hoursModuleOn, hoursState]
  );
  const offersIssues = useMemo(
    () =>
      offersModuleOn
        ? offerSetupIssues({
            flags: offersState.flags,
            recommended: offersState.recommended,
            loadError: offersState.loadError,
          })
        : [],
    [offersModuleOn, offersState]
  );

  const chemicalIssues = useMemo(
    () =>
      chemicalInventorySetupIssues(chemicalState.settings, {
        loadError: chemicalState.loadError,
      }),
    [chemicalState]
  );
  const hseIssues = useMemo(
    () => (hseSettings ? hseKsSetupIssues(hseSettings) : []),
    [hseSettings]
  );

  const showCompany = companyIssues.length > 0;
  const showHours = hoursModuleOn && hoursIssues.length > 0 && !hoursState.loadError;
  const showChemical = chemicalIssues.length > 0 && !chemicalState.loadError;
  const showHse = hseIssues.length > 0 && !hseLoadError;
  const showOffers = offersModuleOn && offersIssues.length > 0 && !offersState.loadError;

  if (loading || (!showCompany && !showHours && !showChemical && !showHse && !showOffers)) {
    return null;
  }

  return (
    <RoleOnly role="admin">
      <div className="space-y-2 sm:space-y-3">
        {showCompany ? (
          <DashboardSetupAlertLink
            to="/dashboard/guide/company#company-setup-status"
            icon={BuildingOffice2Icon}
            title={t("dashboard.company_guide.alert_company_title")}
            count={companyIssues.length}
            subtitle={t("dashboard.company_guide.alert_company_subtitle", { count: companyIssues.length })}
          />
        ) : null}

        {showHours ? (
          <DashboardSetupAlertLink
            to="/dashboard/guide/hours#setup-status"
            icon={ClockIcon}
            title={t("dashboard.company_guide.alert_hours_title")}
            count={hoursIssues.length}
            subtitle={t("dashboard.company_guide.alert_hours_subtitle", { count: hoursIssues.length })}
          />
        ) : null}

        {showChemical ? (
          <DashboardSetupAlertLink
            to="/dashboard/guide/chemical-inventory#chemical-inventory-setup-status"
            icon={BeakerIcon}
            title={t("dashboard.company_guide.alert_chemical_title")}
            count={chemicalIssues.length}
            subtitle={t("dashboard.company_guide.alert_chemical_subtitle", {
              count: chemicalIssues.length,
            })}
          />
        ) : null}

        {showHse ? (
          <DashboardSetupAlertLink
            to="/dashboard/guide/hse-ks#hse-ks-setup-status"
            icon={ShieldCheckIcon}
            title={t("dashboard.company_guide.alert_hse_title")}
            count={hseIssues.length}
            subtitle={t("dashboard.company_guide.alert_hse_subtitle", { count: hseIssues.length })}
          />
        ) : null}

        {showOffers ? (
          <DashboardSetupAlertLink
            to="/dashboard/guide/offers#offers-setup-status"
            icon={DocumentTextIcon}
            title={t("dashboard.company_guide.alert_offers_title")}
            count={offersIssues.length}
            subtitle={t("dashboard.company_guide.alert_offers_subtitle", { count: offersIssues.length })}
          />
        ) : null}
      </div>
    </RoleOnly>
  );
};
